import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useMailDraft, useNow } from './mailHooks';
import { C, S, btn, clampLines } from './mailStyles';
import { emailsLabel, operationVerb, plural, providerName, providerOf } from './mailUtils';

const SENDER_PREVIEW = 5;

function bulkHeadline(action, details) {
  const count = Number(details?.count) || 0;
  if (action === 'trash') return `Move ${emailsLabel(count)} to Trash`;
  return `${operationVerb(details?.operation)} ${emailsLabel(count)}`;
}

function joinNames(names) {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function senderEntries(details) {
  const raw = Array.isArray(details?.senders) ? details.senders : [];
  return raw
    .map((s) => (typeof s === 'string' ? { name: s } : s && typeof s.name === 'string' ? { name: s.name, method: s.method } : null))
    .filter((s) => s && s.name);
}

function UnsubscribeDetails({ details }) {
  const senders = senderEntries(details);
  const methodTotal = ['one_click', 'mailto', 'manual'].reduce((n, k) => n + (Number(details?.[k]) || 0), 0);
  const senderCount = senders.length || methodTotal;
  const providedNames = Array.isArray(details?.manual_names)
    ? details.manual_names.filter((n) => typeof n === 'string' && n.trim())
    : [];
  const manualNames = providedNames.length > 0
    ? providedNames
    : senders.filter((s) => s.method === 'manual').map((s) => s.name);
  const manualCount = Number(details?.manual) || manualNames.length;
  let manualNote = '';
  if (manualNames.length > 0) {
    manualNote = ` (${joinNames(manualNames)} ${manualNames.length === 1 ? 'needs' : 'need'} you to finish manually)`;
  } else if (manualCount > 0) {
    manualNote = ` (${manualCount} ${manualCount === 1 ? 'needs' : 'need'} you to finish manually)`;
  }
  const archiveCount = Number(details?.archive_count) || 0;
  const shown = senders.slice(0, SENDER_PREVIEW);
  const more = senders.length - shown.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: C.bgMuted, borderRadius: 10, padding: '10px 12px' }} data-testid="mail-confirm-unsubscribe">
      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere', ...clampLines(4) }}>
        Unsubscribe from {plural(senderCount, 'sender')}{manualNote}
      </p>
      {shown.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {shown.map((s, i) => (
            <li key={i} style={{ fontSize: 13, color: C.textDim, ...S.ellipsis }}>{s.name}</li>
          ))}
        </ul>
      )}
      {more > 0 && <p style={{ margin: 0, fontSize: 12.5, color: C.textFaint }}>+{more} more</p>}
      {archiveCount > 0 && (
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
          Archive {archiveCount.toLocaleString()} past {archiveCount === 1 ? 'email' : 'emails'} — recoverable in Gmail
        </p>
      )}
    </div>
  );
}

export default function MailConfirmCard({ card, data, sendFollowUp }) {
  const [mountedAt] = useState(() => Date.now());
  const [tapped, setTapped] = useState(null);
  const now = useNow(tapped ? 0 : 1000);

  const action = data?.action;
  const details = data?.details && typeof data.details === 'object' ? data.details : {};
  const isSend = action === 'send_draft';
  const isUnsubscribe = action === 'unsubscribe';
  const isDisconnect = action === 'disconnect';
  const confirmLabel = isSend ? 'Send' : isDisconnect ? 'Disconnect' : 'Confirm';
  const createdAt = typeof card?.createdAt === 'number' && card.createdAt > 0 ? card.createdAt : mountedAt;
  const issuedAt = Number(data?.issued_at);
  const startedAt = Number.isFinite(issuedAt) && issuedAt > 0 ? issuedAt : createdAt;
  const expiresIn = Number(data?.expires_in);
  const expired = Number.isFinite(expiresIn) && expiresIn > 0 && now > startedAt + expiresIn * 1000;
  const hasPreview = typeof details.body_preview === 'string';
  const previewDraftId = typeof details.draft_id === 'string' ? details.draft_id : (typeof data?.draft_id === 'string' ? data.draft_id : '');
  const liveDraft = useMailDraft(previewDraftId, isSend && !hasPreview);
  const bodyPreview = hasPreview ? details.body_preview : (liveDraft.state === 'ok' && typeof liveDraft.draft?.body === 'string' ? liveDraft.draft.body : '');
  const token = typeof data?.confirm_token === 'string' ? data.confirm_token : '';
  const disabled = Boolean(tapped) || expired || !token;

  const onCancel = () => {
    if (disabled) return;
    setTapped('cancel');
    sendFollowUp('cancel', 'Cancel', { mail_action: 'cancel', mail_confirm_action: action || '' });
  };
  const onConfirm = () => {
    if (disabled) return;
    setTapped('confirm');
    sendFollowUp('confirm', confirmLabel, { mail_action: 'confirm', mail_confirm_token: token });
  };

  const subjects = Array.isArray(details.subjects) ? details.subjects.filter((s) => typeof s === 'string').slice(0, 5) : [];
  const count = Number(details.count) || 0;
  const more = count > 5 ? count - subjects.length : 0;

  return (
    <div style={S.card} data-testid="mail-confirm-card">
      <p style={{ ...S.title, ...clampLines(2) }}>
        {isSend
          ? 'Send this email?'
          : isDisconnect ? `Disconnect ${providerName(providerOf(details.provider))}?` : 'Confirm these actions?'}
      </p>
      {!isDisconnect && typeof data?.summary === 'string' && data.summary && (
        <p style={{ ...S.sub, ...clampLines(3) }}>{data.summary}</p>
      )}

      {isSend ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: C.bgMuted, borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
            <span style={S.label}>To</span>
            <span style={{ ...S.ellipsis, fontSize: 15, fontWeight: 700 }}>{details.to || ''}</span>
          </div>
          {details.known_recipient === false && (
            <p style={{ ...S.warn, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <span>You haven't received email from this address before — check the recipient.</span>
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
            <span style={S.label}>Subject</span>
            <span style={{ ...clampLines(2), fontSize: 14, fontWeight: 600 }}>{details.subject || '(no subject)'}</span>
          </div>
          {liveDraft.state === 'loading' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="mail-confirm-preview-loading">
              <div style={{ width: '90%', height: 10, borderRadius: 5, background: C.border }} />
              <div style={{ width: '65%', height: 10, borderRadius: 5, background: C.border }} />
            </div>
          )}
          {bodyPreview && (
            <p data-testid="mail-confirm-preview" style={{ margin: 0, fontSize: 13, color: C.textDim, lineHeight: 1.45, whiteSpace: 'pre-wrap', ...clampLines(4) }}>
              {bodyPreview}
            </p>
          )}
        </div>
      ) : isUnsubscribe ? (
        <UnsubscribeDetails details={details} />
      ) : isDisconnect ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-testid="mail-confirm-disconnect">
          {typeof details.email === 'string' && details.email.trim() && (
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, ...S.ellipsis }}>{details.email.trim()}</p>
          )}
          <p style={S.sub}>Your report and mail data here will be cleared.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: C.bgMuted, borderRadius: 10, padding: '10px 12px' }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{bulkHeadline(action, details)}</p>
          {subjects.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {subjects.map((s, i) => (
                <li key={i} style={{ fontSize: 13, color: C.textDim, ...S.ellipsis }}>{s}</li>
              ))}
            </ul>
          )}
          {more > 0 && <p style={{ margin: 0, fontSize: 12.5, color: C.textFaint }}>+{more} more</p>}
        </div>
      )}

      {expired ? (
        <p style={S.warn}>Expired — ask again</p>
      ) : tapped ? (
        <p style={S.sub}>
          {tapped === 'cancel' ? 'Cancelled' : isSend ? 'Sending…' : isDisconnect ? 'Disconnecting…' : 'Confirmed'}
        </p>
      ) : null}

      <div style={S.actions}>
        <button type="button" style={btn(S.btnSecondary, disabled)} disabled={disabled} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          style={btn(isDisconnect ? { ...S.btnPrimary, background: C.red } : S.btnPrimary, disabled)}
          disabled={disabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
      {isSend && <p style={S.footnote}>Can't undo after sending</p>}
    </div>
  );
}
